<template>
  <div>
    <h1 class="text-2xl font-semibold text-gray-900">Mon panier</h1>
    <div class="mt-6">
      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-zebu-orange"></div>
      </div>
      <div v-else-if="cart.items?.length === 0" class="text-center">
        <p class="text-gray-500">Votre panier est vide.</p>
        <router-link
          to="/subscribe"
          class="mt-4 inline-flex items-center px-4 py-2 border border-zebu-orange rounded-lg text-sm font-medium text-zebu-orange hover:bg-zebu-orange hover:text-zebu-yellow transition-colors duration-300"
        >
          Découvrir nos plans
        </router-link>
      </div>
      <div v-else>
        <div class="bg-white shadow overflow-hidden sm:rounded-lg">
          <ul class="divide-y divide-gray-200">
            <li
              v-for="item in cart.items"
              :key="item.id"
              class="px-4 py-4 sm:px-6"
            >
              <div class="flex items-center justify-between">
                <div class="space-y-3">
                  <div>
                    <h3 class="text-lg font-medium text-gray-900">
                      {{ item.project?.name }}
                    </h3>
                    <p class="text-base text-gray-700 mt-1">
                      {{ item.type?.label }}
                    </p>
                  </div>

                  <!-- Information du pays et cadeau -->
                  <div class="flex flex-col text-sm">
                    <p v-if="item.isGift" class="text-zebu-orange">
                      Cadeau{{ item.beneficiary ? ` pour ${item.beneficiary.name}` : '' }}
                    </p>
                    <p class="text-gray-500">
                      {{ item.project?.country?.name }}
                    </p>
                  </div>

                  <!-- Champ pour nommer le plan -->
                  <div class="flex items-center space-x-2">
                    <div class="relative flex-grow max-w-xs">
                      <div v-if="!item.name" class="absolute -top-2 left-0 transform -translate-y-full">
                        <div class="bg-zebu-orange text-white text-xs px-2 py-1 rounded-md shadow-sm whitespace-nowrap">
                          Personnalisez votre plan !
                          <div class="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-zebu-orange"></div>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2">
                        <input
                          type="text"
                          v-model="item.tempName"
                          :data-plan-id="item.planId"
                          placeholder="Donnez un nom à votre plan"
                          class="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm
                                 focus:border-zebu-orange focus:ring-1 focus:ring-zebu-orange/20
                                 hover:border-gray-300 transition-all duration-200"
                          @keyup.enter="updatePlanName(item)"
                          @blur="updatePlanName(item)"
                        />
                        <button
                          v-if="item.tempName !== item.name"
                          @click="updatePlanName(item)"
                          class="flex-shrink-0 p-2 text-zebu-orange hover:bg-zebu-orange/10 rounded-lg transition-colors duration-200"
                          title="Valider le nom"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-right">
                  <p class="text-lg font-medium text-gray-900">{{ Number(item.amount).toFixed(2) }} €</p>
                  <button
                    @click="removeFromCart(item)"
                    class="mt-2 text-sm text-red-600 hover:text-red-500"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <div class="px-4 py-4 sm:px-6 border-t border-gray-200">
            <div class="flex justify-between items-center">
              <div>
                <span class="text-lg font-medium text-gray-900">Total en €</span>
              </div>
              <span class="text-lg font-medium text-gray-900">{{ cartTotal }} €</span>
            </div>

            <!-- Section modes de paiement -->
            <div class="mt-6 border border-gray-200 rounded-lg p-4">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Mode de paiement</h3>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <!-- Paiement CB -->
                <button
                  @click="selectPaymentMethod('cb')"
                  :disabled="loading"
                  :class="[
                    'flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200',
                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:border-zebu-orange/50',
                    selectedPaymentMethod === 'cb'
                      ? 'border-zebu-orange bg-zebu-orange/5'
                      : 'border-gray-200'
                  ]"
                >
                  <div class="relative">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/>
                      <line x1="2" y1="10" x2="22" y2="10"/>
                    </svg>
                    <div v-if="loading && selectedPaymentMethod === 'cb'" class="absolute inset-0 flex items-center justify-center">
                      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-zebu-orange"></div>
                    </div>
                  </div>
                  <span class="font-medium">Paiement CB</span>
                </button>

                <!-- Paiement par chèque -->
                <button
                  @click="selectPaymentMethod('cheque')"
                  :disabled="loading"
                  :class="[
                    'flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200',
                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:border-zebu-orange/50',
                    selectedPaymentMethod === 'cheque'
                      ? 'border-zebu-orange bg-zebu-orange/5'
                      : 'border-gray-200'
                  ]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="6" width="20" height="12" rx="2"/>
                    <line x1="6" y1="12" x2="18" y2="12"/>
                  </svg>
                  <span class="font-medium">Paiement par chèque</span>
                </button>

                <!-- Paiement par virement -->
                <div class="relative">
                  <button
                    disabled
                    class="w-full flex flex-col items-center justify-center p-4 rounded-lg border-2 border-gray-200 opacity-50 cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M2 17h2m4 0h2m4 0h2m4 0h2"/>
                      <path d="M12 6v8m0-8l4 4m-4-4L8 10"/>
                    </svg>
                    <span class="font-medium">Paiement par virement</span>
                    <span class="absolute top-1 right-1 text-xs text-gray-500">Bientôt</span>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="checkoutError" class="mt-4 p-4 bg-red-50 rounded-lg">
              <p class="text-sm text-red-600">{{ checkoutError }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour le paiement par chèque -->
    <div v-if="showChequeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full p-6 space-y-6">
        <div class="flex justify-between items-start">
          <h3 class="text-xl font-semibold text-gray-900">Paiement par chèque</h3>
          <button @click="showChequeModal = false" class="text-gray-400 hover:text-gray-500">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="space-y-4">
          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">Instructions pour le paiement</h4>
            <p class="text-gray-600">Veuillez envoyer votre chèque à l'ordre de :</p>
            <div class="mt-2 p-3 bg-white rounded border border-gray-200">
              <p class="font-medium">Association Zebunet</p>
              <p>1 Quai des Champs Elysées</p>
              <p>75008 Paris</p>
              <p>France</p>
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">Montant à payer</h4>
            <p class="text-2xl font-bold text-zebu-orange">{{ cartTotal }} €</p>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">Important</h4>
            <ul class="list-disc list-inside text-gray-600 space-y-1">
              <li>Votre réservation sera confirmée à réception du chèque</li>
              <li>Après confirmation de votre commande, vous recevrez une référence unique à indiquer au dos de votre chèque</li>
              <li>Le traitement peut prendre jusqu'à 5 jours ouvrés</li>
            </ul>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">Prochaines étapes</h4>
            <ol class="list-decimal list-inside text-gray-600 space-y-1">
              <li>Vous allez être redirigé vers une page de confirmation</li>
              <li>Cette page contiendra votre référence unique et toutes les informations pour l'envoi du chèque</li>
              <li>Vous recevrez également ces informations par email</li>
            </ol>
          </div>
        </div>

        <div class="flex justify-end space-x-4">
          <button 
            @click="showChequeModal = false"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button 
            @click="confirmChequePayment"
            class="px-4 py-2 bg-zebu-orange text-white rounded-lg hover:bg-zebu-orange-dark"
          >
            Confirmer la commande
          </button>
        </div>
      </div>
    </div>

    <!-- Modal d'erreur -->
    <div v-if="showErrorModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-semibold text-gray-900">Une erreur est survenue</h3>
          <button @click="showErrorModal = false" class="text-gray-400 hover:text-gray-500">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="flex items-center justify-center space-x-4 p-4 bg-red-50 rounded-lg">
          <span class="text-2xl">😢</span>
          <p class="text-center text-red-600">Une erreur de notre côté est survenue sur ce plan, nous avons reçu une alerte et allons régler le problème très rapidement.</p>
          <span class="text-2xl">😢</span>
        </div>

        <div class="mt-6 flex justify-end">
          <button 
            @click="showErrorModal = false"
            class="px-4 py-2 bg-zebu-orange text-white rounded-lg hover:bg-zebu-orange-dark"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { memberareaService } from '@/services/memberarea.service'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'

const router = useRouter()
const toast = useToast()
const loading = ref(true)
const checkoutError = ref(null)
const selectedPaymentMethod = ref('cb')
const showChequeModal = ref(false)
const showErrorModal = ref(false)
const cart = ref({
  items: [],
  total: 0
})

// Calcul correct du total du panier
const cartTotal = computed(() => {
  if (!cart.value.items?.length) return '0.00'
  const total = cart.value.items.reduce((sum, item) => {
    return sum + Number(item.amount)
  }, 0)
  return total.toFixed(2)
})

const removeFromCart = async (item) => {
  try {
    loading.value = true
    await memberareaService.removeFromCart(item.id)
    // Recharger le panier après la suppression
    const response = await memberareaService.getCart()
    if (response.success && response.data) {
      cart.value = response.data
    }
  } catch (error) {
    console.error('Error removing item from cart:', error)
  } finally {
    loading.value = false
  }
}

const checkout = async () => {
  try {
    checkoutError.value = null
    loading.value = true

    const response = await memberareaService.checkoutCart()
    
    if (response.success && response.url) {
      // Stocker les IDs des plans pour la gestion d'erreur
      const planIds = cart.value.items.map(item => item.planId)
      localStorage.setItem('cartPlanIds', JSON.stringify(planIds))
      
      window.location.href = response.url
      return
    }
    
    console.error('Format de réponse checkout invalide:', response)
    handleCheckoutError(new Error('Une erreur est survenue lors de la préparation du paiement. Veuillez réessayer.'))
  } catch (error) {
    console.error('Erreur lors du checkout:', error)
    handleCheckoutError(error)
  } finally {
    loading.value = false
  }
}

const selectPaymentMethod = async (method) => {
  selectedPaymentMethod.value = method
  if (method === 'cb') {
    await checkout()
  } else if (method === 'cheque') {
    handleChequePayment()
  }
}

const handleChequePayment = () => {
  showChequeModal.value = true
}

const confirmChequePayment = async () => {
  try {
    loading.value = true

    // Préparer les données pour l'API
    const items = cart.value.items.map(item => ({
      plan_id: item.planId,
      amount: Number(item.amount),
      is_gift: item.isGift || false,
      ...(item.isGift && item.beneficiary ? {
        beneficiary: {
          email: item.beneficiary.email,
          firstname: item.beneficiary.firstname || item.beneficiary.name.split(' ')[0],
          lastname: item.beneficiary.lastname || item.beneficiary.name.split(' ')[1] || '',
          ...(item.beneficiary.id && { id: item.beneficiary.id })
        },
        gift_message: item.giftMessage || ''
      } : {})
    }))

    const response = await memberareaService.checkPayment({ cart: items })
    
    if (response.success) {
      showChequeModal.value = false
      // Rediriger vers la page de succès avec les informations du paiement
      router.push({
        name: 'PaymentSuccess',
        query: {
          type: 'cheque',
          reference: response.payment.reference,
          amount: response.payment.amount,
          status: response.payment.status,
          message: response.message
        }
      })
    } else {
      throw new Error(response.message || 'Une erreur est survenue')
    }
  } catch (error) {
    console.error('Erreur lors du paiement par chèque:', error)
    // Rediriger vers la page d'échec en cas d'erreur
    router.push({
      name: 'PaymentFailed',
      query: {
        type: 'cheque',
        error: error.message || 'Une erreur est survenue lors du traitement de votre commande'
      }
    })
  } finally {
    loading.value = false
  }
}

// Ajout de la fonction pour mettre à jour le nom du plan
const updatePlanName = async (item) => {
  try {
    // Si le nom n'a pas changé, ne rien faire
    if (item.tempName === item.name) {
      return;
    }

    // Validation de la longueur du nom
    if (item.tempName && item.tempName.length > 50) {
      toast.warning('Le nom du plan ne peut pas dépasser 50 caractères')
      // Réinitialiser tempName à la valeur actuelle
      item.tempName = item.name;
      return;
    }

    const response = await memberareaService.updatePlanName(item.planId, item.tempName)
    if (response.success) {
      // Mettre à jour le nom du plan
      item.name = item.tempName;
      // Notification subtile pour le blur
      if (document.activeElement !== document.querySelector(`input[data-plan-id="${item.planId}"]`)) {
        toast.success('Nom du plan mis à jour', {
          timeout: 2000,
          hideProgressBar: true
        });
      }
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour du nom du plan:', error);
    toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du nom du plan');
    // En cas d'erreur, réinitialiser tempName à la valeur actuelle du name
    item.tempName = item.name;
  }
};

// Modifier la gestion des erreurs dans la fonction de checkout pour afficher la modal
const handleCheckoutError = (error) => {
  if (error?.response?.data?.message === "Une erreur de notre côté est survenue sur ce plan, nous avons reçu une alerte et allons régler le problème très rapidement.") {
    showErrorModal.value = true
  } else {
    checkoutError.value = error?.response?.data?.message || 'Une erreur est survenue'
  }
}

onMounted(async () => {
  try {
    loading.value = true
    const response = await memberareaService.getCart()
    if (response.success && response.data) {
      // Initialiser le panier avec tempName pour chaque item
      const itemsWithTemp = response.data.items.map(item => ({
        ...item,
        tempName: item.name || ''
      }))
      cart.value = {
        ...response.data,
        items: itemsWithTemp
      }
    }
  } catch (error) {
    console.error('Error loading cart:', error)
  } finally {
    loading.value = false
  }
})
</script> 